import { useLotCalculator } from "@/hooks/useLotCalculator";
import { LotCalculatorForm } from "@/components/tools/lot-calculator/LotCalculatorForm";
import { ResultSummary } from "@/components/tools/lot-calculator/ResultSummary";
import { TipBox } from "@/components/tools/lot-calculator/TipBox";
import { FirmRecommendations } from "@/components/tools/lot-calculator/FirmRecommendations";
import { useUI } from "@/contexts/UIContext";
import { RiskManagementSummary } from "@/components/tools/lot-calculator/RiskManagementSummary";
import { Helmet } from 'react-helmet-async';

const LotCalculatorPage = () => {
  const { result, calculate } = useLotCalculator();
  const { isLoading } = useUI();

  // Debug: log result state
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-console
    console.log('LotCalculatorPage result:', result);
  }

  return (
    <>
      <Helmet>
        <title>Prop Firm Lot Size & Risk Calculator | PropViper</title>
        <meta name="description" content="Calculate your optimal lot size, risk, and margin for any prop firm challenge. Fast, accurate, and free forex calculator for traders." />
        <meta property="og:title" content="Prop Firm Lot Size & Risk Calculator" />
        <meta property="og:description" content="Calculate your optimal lot size, risk, and margin for any prop firm challenge. Fast, accurate, and free forex calculator for traders." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://propviper.com/tools/lot-calculator" />
        <meta property="og:image" content="https://propviper.com/og-image-lot-calculator.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Prop Firm Lot Size & Risk Calculator" />
        <meta name="twitter:description" content="Calculate your optimal lot size, risk, and margin for any prop firm challenge." />
        <meta name="twitter:image" content="https://propviper.com/og-image-lot-calculator.png" />
        <link rel="canonical" href="https://www.propviper.com/tools/lot-calculator" />
      </Helmet>
      <div className="min-h-screen bg-gray-950 dark:bg-neutral-900 transition-colors">
        <section className="bg-gradient-to-br from-black to-gray-900 py-12 sm:py-16 px-2 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-4 text-yellow-400 text-center">
              Prop Firm Lot Size &amp; Risk Calculator
            </h1>
            <p className="text-base sm:text-lg text-yellow-200 mb-6 text-center">
              Calculate your optimal lot size, risk, and margin for any prop firm challenge.
            </p>
          </div>
        </section>

        <section className="bg-gray-950 dark:bg-neutral-900 py-10">
          <div className="max-w-5xl mx-auto px-2 md:px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-[400px]">
              {/* ── Form column ── */}
              <div className="flex flex-col bg-[#232323] dark:bg-neutral-800 rounded-2xl shadow-md p-2 md:p-6">
                {/* Error/Warning Display */}
                {result?.error && !result.error.startsWith('Warning') && (
                  <div className="mb-2 rounded-lg px-3 py-2 text-xs md:text-sm font-semibold flex items-center gap-2 bg-red-200 text-red-900"> 
                    <span>{result.error}</span>
                  </div>
                )}
                {result?.error && result.error.startsWith('Warning') && (
                  <div className="mb-2 rounded-lg px-3 py-2 text-xs md:text-sm font-semibold flex items-center gap-2 bg-yellow-200 text-yellow-900"> 
                    <span>{result.error}</span>
                  </div>
                )}
                <div className="flex-1 min-h-0">
                  <LotCalculatorForm onCalculate={calculate} />
                </div>
              </div>

              {/* ── Result column ── */}
              {(result && (!result.error || (result.error && result.error.startsWith('Warning')))) && (
                <div className="flex flex-col space-y-2 md:space-y-4 px-1 md:px-2 h-full min-h-0">
                  <div className="flex-shrink-0">
                    <ResultSummary result={result} />
                  </div>
                  <div className="flex-shrink-0">
                    <RiskManagementSummary result={result} />
                  </div>
                  <div className="flex-shrink-0">
                    <TipBox result={result} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Per-Trade Breakdown Table - Full Width Below Main Calculator */}
        <div className="bg-gray-950 dark:bg-neutral-900">
          {(result && (!result.error || (result.error && result.error.startsWith('Warning'))) && result.trades && Array.isArray(result.trades) && result.trades.length > 0) && (
            <div className="pt-4 pb-4 flex justify-center">
              <div className="bg-[#232323] dark:bg-neutral-800 rounded-2xl shadow-md p-2 md:p-4 max-w-5xl w-full mx-auto">
                <div className="mb-1 md:mb-2 text-base md:text-lg font-semibold text-yellow-300">Per-Trade Breakdown</div>
                <div className="overflow-x-auto -mx-2 md:mx-0">
                  <div className="min-w-full inline-block align-middle">
                    <div className="overflow-hidden">
                      <table className="min-w-full text-xs md:text-sm border-separate border-spacing-y-1 md:border-spacing-y-2 bg-[#232323] dark:bg-neutral-800">
                        <thead>
                          <tr className="text-neutral-300 bg-[#232323] dark:bg-neutral-800 sticky top-0">
                            <th className="p-1 md:p-2 text-left min-w-[40px] md:min-w-[50px]">#</th>
                            <th className="p-1 md:p-2 text-left min-w-[80px] md:min-w-[100px]">Pair</th>
                            <th className="p-1 md:p-2 text-left min-w-[70px] md:min-w-[80px]">Direction</th>
                            <th className="p-1 md:p-2 text-left min-w-[90px] md:min-w-[100px]">Entry Price</th>
                            <th className="p-1 md:p-2 text-left min-w-[90px] md:min-w-[100px]">Stop Loss</th>
                            <th className="p-1 md:p-2 text-left min-w-[70px] md:min-w-[80px]">Lot Size</th>
                            <th className="p-1 md:p-2 text-left min-w-[70px] md:min-w-[80px]">Pip Dist</th>
                            <th className="p-1 md:p-2 text-left min-w-[70px] md:min-w-[80px]">Risk $</th>
                            <th className="p-1 md:p-2 text-left min-w-[90px] md:min-w-[100px]">Margin Req</th>
                            <th className="p-1 md:p-2 text-left min-w-[60px] md:min-w-[80px]">Warn</th>
                          </tr>
                        </thead>
                        <tbody className="text-neutral-100">
                          {result.trades.map((t: any, i: number) => {
                            // Format numbers based on pair type
                            const isJPYPair = t.pair?.includes('JPY') || false;
                            const priceDecimals = isJPYPair ? 2 : 4;
                            
                            // Safe number formatting functions with type checking
                            const formatPrice = (price: any) => {
                              if (typeof price !== 'number' && typeof price !== 'string') return '0.0000';
                              const num = typeof price === 'string' ? parseFloat(price) : price;
                              return isNaN(num) ? '0.0000' : num.toFixed(priceDecimals);
                            };

                            const formatLot = (lot: any) => {
                              if (typeof lot !== 'number' && typeof lot !== 'string') return '0.00';
                              const num = typeof lot === 'string' ? parseFloat(lot) : lot;
                              return isNaN(num) ? '0.00' : num.toFixed(2);
                            };

                            const formatMargin = (margin: any) => {
                              if (typeof margin !== 'number' && typeof margin !== 'string') return '0.00';
                              const num = typeof margin === 'string' ? parseFloat(margin) : margin;
                              return isNaN(num) ? '0.00' : num.toFixed(2);
                            };

                            const formatRisk = (risk: any) => {
                              if (typeof risk !== 'number' && typeof risk !== 'string') return '0.00';
                              const num = typeof risk === 'string' ? parseFloat(risk) : risk;
                              return isNaN(num) ? '0.00' : num.toFixed(2);
                            };

                            return (
                              <tr key={i} className="bg-[#292929] dark:bg-neutral-700 rounded-xl">
                                <td className="p-1 md:p-2 font-bold text-yellow-300">{i+1}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{t.pair || '-'}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{t.direction || '-'}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{formatPrice(t.entryPrice)}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{formatPrice(t.stopLossPrice)}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{formatLot(t.lotSize)}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{typeof t.pipDist === 'number' ? Math.round(t.pipDist) : '-'}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{formatRisk(t.riskValue)}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">{formatMargin(t.marginRequired)}</td>
                                <td className="p-1 md:p-2 whitespace-nowrap">
                                  {t.unsupported && <span className="text-yellow-400 font-semibold">?</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                {result.unsupportedSymbols && result.unsupportedSymbols.length > 0 && (
                  <div className="mt-1 md:mt-2 text-xs text-yellow-300">Some pairs use default pip/margin values: {result.unsupportedSymbols.join(', ')}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Full Width New Section: Firm Suggestions */}
        {result && (
          <div className="bg-gray-950 dark:bg-neutral-900 pb-12">
            <div className="max-w-5xl w-full mx-auto">
              <FirmRecommendations result={result} />
              {/* Disclaimer Section - always below FirmRecommendations */}
              <div className="bg-neutral-800 border border-yellow-400 text-white rounded-xl max-w-4xl mx-auto mt-4 mb-0 p-4 text-sm md:text-base text-center font-semibold shadow-md">
                <strong>Disclaimer:</strong> This calculator is provided for informational and educational purposes only. While we strive for accuracy, we do not guarantee the results and are not responsible for any losses or damages resulting from its use. The calculator is currently in development and testing—please double-check all calculations before making trading decisions.
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-neutral-800 p-4 rounded-lg shadow-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LotCalculatorPage;
